import React, { useState, useEffect } from 'react';
import { emailTemplatesAPI, notificationAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatRelativeTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Mail,
  RefreshCw,
  Plus,
  Edit,
  Trash,
  Eye,
  Send,
  AlertTriangle,
  FileText,
  BarChart3,
  Code,
  Palette,
  CheckCircle,
  XCircle,
  Copy,
  ArrowLeft,
  History,
  RotateCcw,
  GitCompare,
  Clock,
  User,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const TEMPLATE_TYPES = [
  { value: 'alert_notification', label: 'Alert Notification', icon: AlertTriangle, color: 'text-orange-400' },
  { value: 'case_assignment', label: 'Case Assignment', icon: FileText, color: 'text-blue-400' },
  { value: 'scheduled_report', label: 'Scheduled Report', icon: BarChart3, color: 'text-green-400' },
];

const VARIABLE_DOCS = {
  alert_notification: [
    { name: 'alert_title', description: 'Title of the alert' },
    { name: 'severity', description: 'Alert severity (CRITICAL, HIGH, MEDIUM, LOW)' },
    { name: 'severity_color', description: 'Color for severity indicator' },
    { name: 'description', description: 'Alert description text' },
    { name: 'alert_type', description: 'Type of alert (e.g., suspicious_transaction)' },
    { name: 'created_at', description: 'When the alert was created' },
    { name: 'action_url', description: 'Link to view the alert' },
  ],
  case_assignment: [
    { name: 'case_number', description: 'Unique case identifier' },
    { name: 'case_title', description: 'Title of the case' },
    { name: 'priority', description: 'Case priority (CRITICAL, HIGH, MEDIUM, LOW)' },
    { name: 'priority_color', description: 'Color for priority indicator' },
    { name: 'status', description: 'Current case status' },
    { name: 'description', description: 'Case description text' },
    { name: 'action_url', description: 'Link to view the case' },
  ],
  scheduled_report: [
    { name: 'report_type', description: 'Type of report' },
    { name: 'date', description: 'Report date' },
    { name: 'generated_at', description: 'Timestamp when generated' },
    { name: 'total_entities', description: 'Number of entities' },
    { name: 'total_transactions', description: 'Number of transactions' },
    { name: 'active_alerts', description: 'Number of active alerts' },
    { name: 'open_cases', description: 'Number of open cases' },
    { name: 'risk_summary', description: 'Risk summary text' },
    { name: 'action_url', description: 'Link to full report' },
  ],
};

const STYLE_VARIABLES = [
  { name: 'bg_color', description: 'Background color', default: '#0a0a0a' },
  { name: 'card_bg', description: 'Card background color', default: '#1a1a2e' },
  { name: 'border_color', description: 'Border color', default: '#333' },
  { name: 'primary_color', description: 'Primary accent color', default: '#00F0FF' },
  { name: 'button_gradient', description: 'Button gradient', default: 'linear-gradient(135deg, #00F0FF 0%, #0066FF 100%)' },
  { name: 'footer_text', description: 'Footer text', default: 'AEGIS AML/CTF Intelligence Platform' },
];

export default function EmailTemplates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailStatus, setEmailStatus] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [editorTab, setEditorTab] = useState('visual');

  // Version History State
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState({ current: null, history: [] });
  const [selectedVersionPreview, setSelectedVersionPreview] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareVersions, setCompareVersions] = useState({ a: null, b: null });
  const [comparisonResult, setComparisonResult] = useState(null);

  const [editForm, setEditForm] = useState({
    name: '',
    type: 'alert_notification',
    subject: '',
    html_template: '',
    variables: {},
  });

  useEffect(() => {
    fetchTemplates();
    fetchEmailStatus();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await emailTemplatesAPI.list();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailStatus = async () => {
    try {
      const response = await notificationAPI.getEmailStatus();
      setEmailStatus(response.data);
    } catch (error) {
      console.error('Failed to get email status:', error);
    }
  };

  // Version History Functions
  const fetchVersionHistory = async (templateId) => {
    try {
      const response = await emailTemplatesAPI.listVersions(templateId);
      setVersionHistory(response.data);
    } catch (error) {
      toast.error('Failed to load version history');
    }
  };

  const handleShowVersionHistory = async (template) => {
    setSelectedTemplate(template);
    await fetchVersionHistory(template.id);
    setShowVersionHistory(true);
    setCompareMode(false);
    setComparisonResult(null);
  };

  const handlePreviewVersion = async (templateId, versionId) => {
    try {
      const response = await emailTemplatesAPI.previewVersion(templateId, versionId);
      setSelectedVersionPreview(response.data);
    } catch (error) {
      toast.error('Failed to preview version');
    }
  };

  const handleRevertToVersion = async (templateId, versionId, versionNum) => {
    if (!window.confirm(`Are you sure you want to revert to version ${versionNum}? This will create a new version from the current state.`)) {
      return;
    }
    try {
      await emailTemplatesAPI.revertToVersion(templateId, versionId);
      toast.success(`Reverted to version ${versionNum}`);
      await fetchVersionHistory(templateId);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to revert version');
    }
  };

  const handleCompareVersions = async () => {
    if (!compareVersions.a || !compareVersions.b) {
      toast.error('Please select two versions to compare');
      return;
    }
    try {
      const response = await emailTemplatesAPI.compareVersions(
        selectedTemplate.id,
        compareVersions.a,
        compareVersions.b
      );
      setComparisonResult(response.data);
    } catch (error) {
      toast.error('Failed to compare versions');
    }
  };

  const handlePreview = async (template) => {
    try {
      const response = await emailTemplatesAPI.preview(template.id);
      setPreviewHtml(response.data.html);
      setPreviewSubject(response.data.subject);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const handleSendTest = async (templateId) => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setSending(true);
    try {
      await emailTemplatesAPI.sendTest(templateId, testEmail);
      toast.success('Test email sent successfully');
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditForm({
      id: template.id,
      name: template.name,
      type: template.type,
      subject: template.subject,
      html_template: template.html_template,
      variables: template.variables || {},
    });
    setSelectedTemplate(template);
    setShowEditor(true);
  };

  const handleSaveTemplate = async () => {
    try {
      if (editForm.id) {
        await emailTemplatesAPI.update(editForm.id, {
          name: editForm.name,
          subject: editForm.subject,
          html_template: editForm.html_template,
          variables: JSON.stringify(editForm.variables),
        });
        toast.success('Template updated');
      } else {
        await emailTemplatesAPI.create({
          name: editForm.name,
          type: editForm.type,
          subject: editForm.subject,
          html_template: editForm.html_template,
          variables: JSON.stringify(editForm.variables),
        });
        toast.success('Template created');
      }
      setShowEditor(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await emailTemplatesAPI.update(template.id, { is_active: !template.is_active });
      toast.success(`Template ${template.is_active ? 'disabled' : 'enabled'}`);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (template.is_default) {
      toast.error('Cannot delete default template');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await emailTemplatesAPI.delete(template.id);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const copyVariable = (varName) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    toast.success(`Copied {{${varName}}} to clipboard`);
  };

  const getTemplateTypeConfig = (type) => {
    return TEMPLATE_TYPES.find(t => t.value === type) || TEMPLATE_TYPES[0];
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-4">
          <div className="h-8 w-8 bg-card rounded skeleton" />
          <div className="h-8 w-48 bg-card rounded skeleton" />
        </div>
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-card rounded skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="email-templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/settings" className="text-muted-foreground hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
              Email Templates
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Customize notification emails for alerts, cases, and reports
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Email Status Badge */}
          {emailStatus && (
            <Badge
              variant="outline"
              className={`text-xs ${emailStatus.mock_mode ? 'text-yellow-400 border-yellow-400/30' : 'text-green-400 border-green-400/30'}`}
            >
              {emailStatus.mock_mode ? (
                <>
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Mock Mode
                </>
              ) : (
                <>
                  <CheckCircle className="w-3 h-3 mr-1" />
                  SendGrid Active
                </>
              )}
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={fetchTemplates} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Mock Mode Warning */}
      {emailStatus?.mock_mode && (
        <Card className="glass border-yellow-500/30 bg-yellow-500/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-400">Email Mock Mode Active</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Emails are being logged to the database instead of sent. Add SENDGRID_API_KEY to enable real email delivery.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TEMPLATE_TYPES.map((templateType) => {
          const template = templates.find(t => t.type === templateType.value && t.is_active);
          const Icon = templateType.icon;
          
          return (
            <Card key={templateType.value} className="glass card-hover">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg bg-white/5 ${templateType.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  {template && (
                    <Badge variant="outline" className="text-[10px] text-green-400 border-green-400/30">
                      Active
                    </Badge>
                  )}
                </div>
                <h3 className="text-white font-medium mb-1">{templateType.label}</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  {template?.name || 'Default template'}
                </p>
                <div className="flex gap-2">
                  {template && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handlePreview(template)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleEditTemplate(template)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* All Templates Table */}
      <Card className="glass">
        <CardHeader className="border-b border-white/5 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="font-secondary text-sm uppercase tracking-wider">
              All Templates
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => {
                  const typeConfig = getTemplateTypeConfig(template.type);
                  const Icon = typeConfig.icon;
                  
                  return (
                    <TableRow key={template.id} className="border-white/5">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded bg-white/5 ${typeConfig.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{template.name}</p>
                            {template.is_default && (
                              <Badge variant="outline" className="text-[10px] mt-1">
                                Default
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {typeConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={template.is_active}
                            onCheckedChange={() => handleToggleActive(template)}
                          />
                          <span className={`text-xs ${template.is_active ? 'text-green-400' : 'text-muted-foreground'}`}>
                            {template.is_active ? 'Active' : 'Disabled'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {template.updated_at ? formatRelativeTime(template.updated_at) : 'Never'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePreview(template)}
                            title="Preview"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditTemplate(template)}
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleShowVersionHistory(template)}
                            title="Version History"
                          >
                            <History className="w-4 h-4" />
                          </Button>
                          {!template.is_default && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteTemplate(template)}
                              title="Delete"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="glass border-border max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-secondary text-lg font-bold uppercase tracking-wide">
              Email Preview
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-black/20 px-4 py-2 rounded border border-white/10">
              <p className="text-xs text-muted-foreground">Subject:</p>
              <p className="text-white">{previewSubject}</p>
            </div>
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <iframe
                srcDoc={previewHtml}
                className="w-full h-[500px] bg-white"
                title="Email Preview"
              />
            </div>
            <div className="flex items-center gap-3 pt-4 border-t border-white/10">
              <Input
                type="email"
                placeholder="Enter email to send test"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="bg-black/20 border-white/10 flex-1"
              />
              <Button
                onClick={() => selectedTemplate && handleSendTest(selectedTemplate.id)}
                disabled={sending || !testEmail}
                className="gap-2"
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Test
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Editor Modal */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="glass border-border max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary text-lg font-bold uppercase tracking-wide">
              {editForm.id ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-mono">Template Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="e.g., Custom Alert Template"
                  className="bg-black/20 border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-mono">Email Subject</Label>
                <Input
                  value={editForm.subject}
                  onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                  placeholder="e.g., [AEGIS] Alert: {{alert_title}}"
                  className="bg-black/20 border-white/10 font-mono text-sm"
                />
              </div>
            </div>

            {/* Editor Tabs */}
            <Tabs value={editorTab} onValueChange={setEditorTab}>
              <TabsList className="bg-black/20">
                <TabsTrigger value="visual" className="gap-2">
                  <Palette className="w-4 h-4" />
                  Style Variables
                </TabsTrigger>
                <TabsTrigger value="code" className="gap-2">
                  <Code className="w-4 h-4" />
                  HTML Template
                </TabsTrigger>
              </TabsList>

              {/* Style Variables Tab */}
              <TabsContent value="visual" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {STYLE_VARIABLES.map((variable) => (
                    <div key={variable.name} className="space-y-2">
                      <Label className="label-mono text-xs">{variable.description}</Label>
                      <div className="flex gap-2">
                        <Input
                          value={editForm.variables[variable.name] || variable.default}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            variables: { ...editForm.variables, [variable.name]: e.target.value }
                          })}
                          className="bg-black/20 border-white/10 font-mono text-xs flex-1"
                        />
                        {variable.name.includes('color') && (
                          <input
                            type="color"
                            value={editForm.variables[variable.name] || variable.default}
                            onChange={(e) => setEditForm({
                              ...editForm,
                              variables: { ...editForm.variables, [variable.name]: e.target.value }
                            })}
                            className="w-10 h-10 rounded border border-white/10 bg-transparent cursor-pointer"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* HTML Template Tab */}
              <TabsContent value="code" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2 space-y-2">
                    <Label className="label-mono">HTML Template</Label>
                    <Textarea
                      value={editForm.html_template}
                      onChange={(e) => setEditForm({ ...editForm, html_template: e.target.value })}
                      className="bg-black/20 border-white/10 font-mono text-xs min-h-[400px]"
                      placeholder="<html>..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-mono">Available Variables</Label>
                    <div className="bg-black/20 border border-white/10 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                      <p className="text-xs text-muted-foreground mb-3">
                        Click to copy. Use <code className="bg-white/10 px-1 rounded">{'{{variable}}'}</code> syntax.
                      </p>
                      <div className="space-y-2">
                        {(VARIABLE_DOCS[editForm.type] || []).map((v) => (
                          <button
                            key={v.name}
                            onClick={() => copyVariable(v.name)}
                            className="w-full text-left p-2 rounded hover:bg-white/5 transition-colors group"
                          >
                            <div className="flex items-center justify-between">
                              <code className="text-primary text-xs">{`{{${v.name}}}`}</code>
                              <Copy className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1">{v.description}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button variant="ghost" onClick={() => setShowEditor(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveTemplate}>
                Save Template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version History Modal */}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="glass border-border max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-secondary text-lg font-bold uppercase tracking-wide flex items-center gap-3">
              <History className="w-5 h-5 text-primary" />
              Version History - {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex gap-4 mt-4 h-[600px]">
            {/* Version List */}
            <div className="w-1/3 border-r border-white/10 pr-4 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-white">Versions</h3>
                <Button
                  variant={compareMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setCompareMode(!compareMode);
                    setCompareVersions({ a: null, b: null });
                    setComparisonResult(null);
                  }}
                  className="gap-2"
                >
                  <GitCompare className="w-4 h-4" />
                  {compareMode ? 'Exit Compare' : 'Compare'}
                </Button>
              </div>
              
              {compareMode && (
                <div className="mb-4 p-3 bg-primary/10 border border-primary/30 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">
                    Select two versions to compare
                  </p>
                  <div className="flex gap-2">
                    <Badge variant="outline" className={`text-xs ${compareVersions.a ? 'text-green-400' : ''}`}>
                      A: {compareVersions.a ? `v${versionHistory.history?.find(v => v.id === compareVersions.a)?.version || 'current'}` : 'Select'}
                    </Badge>
                    <Badge variant="outline" className={`text-xs ${compareVersions.b ? 'text-blue-400' : ''}`}>
                      B: {compareVersions.b ? `v${versionHistory.history?.find(v => v.id === compareVersions.b)?.version || 'current'}` : 'Select'}
                    </Badge>
                  </div>
                  {compareVersions.a && compareVersions.b && (
                    <Button size="sm" onClick={handleCompareVersions} className="w-full mt-2">
                      Compare Versions
                    </Button>
                  )}
                </div>
              )}

              {/* Current Version */}
              {versionHistory.current && (
                <div
                  className={`p-3 rounded-lg mb-2 cursor-pointer transition-colors ${
                    selectedVersionPreview?.is_current 
                      ? 'bg-primary/20 border border-primary/50' 
                      : 'bg-white/5 hover:bg-white/10 border border-transparent'
                  }`}
                  onClick={() => {
                    if (compareMode) {
                      if (!compareVersions.a) {
                        setCompareVersions({ ...compareVersions, a: 'current' });
                      } else if (!compareVersions.b && compareVersions.a !== 'current') {
                        setCompareVersions({ ...compareVersions, b: 'current' });
                      }
                    } else {
                      handlePreviewVersion(selectedTemplate?.id, 'current');
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-500/20 text-green-400 text-[10px]">Current</Badge>
                      <span className="text-xs font-mono text-muted-foreground">
                        v{versionHistory.current.version}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-white mt-1 truncate">{versionHistory.current.name}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {formatRelativeTime(versionHistory.current.created_at)}
                  </p>
                </div>
              )}

              {/* Historical Versions */}
              {versionHistory.history?.length > 0 ? (
                <div className="space-y-2">
                  {versionHistory.history.map((version) => (
                    <div
                      key={version.id}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedVersionPreview?.version === version.version && !selectedVersionPreview?.is_current
                          ? 'bg-primary/20 border border-primary/50'
                          : 'bg-white/5 hover:bg-white/10 border border-transparent'
                      }`}
                      onClick={() => {
                        if (compareMode) {
                          if (!compareVersions.a) {
                            setCompareVersions({ ...compareVersions, a: version.id });
                          } else if (!compareVersions.b && compareVersions.a !== version.id) {
                            setCompareVersions({ ...compareVersions, b: version.id });
                          }
                        } else {
                          handlePreviewVersion(selectedTemplate?.id, version.id);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono text-primary">v{version.version}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRevertToVersion(selectedTemplate?.id, version.id, version.version);
                          }}
                          title="Revert to this version"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-sm text-white mt-1 truncate">{version.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{version.change_summary}</p>
                      <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {formatRelativeTime(version.created_at)}
                        {version.created_by_name && (
                          <>
                            <User className="w-3 h-3 ml-2" />
                            {version.created_by_name}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No version history yet</p>
                  <p className="text-xs">Edit the template to create versions</p>
                </div>
              )}
            </div>

            {/* Preview / Comparison Panel */}
            <div className="flex-1 overflow-y-auto">
              {comparisonResult ? (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-white">Comparison Results</h3>
                  <div className="flex items-center gap-4 text-sm">
                    <Badge variant="outline" className="text-green-400">
                      v{comparisonResult.version_a.version} - {comparisonResult.version_a.name}
                    </Badge>
                    <span className="text-muted-foreground">vs</span>
                    <Badge variant="outline" className="text-blue-400">
                      v{comparisonResult.version_b.version} - {comparisonResult.version_b.name}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {comparisonResult.total_changes} change{comparisonResult.total_changes !== 1 ? 's' : ''} detected
                  </div>
                  
                  <div className="space-y-3">
                    {Object.entries(comparisonResult.differences).map(([field, diff]) => (
                      <div key={field} className={`p-3 rounded-lg ${diff.changed ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-white/5'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium capitalize">{field.replace('_', ' ')}</span>
                          {diff.changed ? (
                            <Badge variant="outline" className="text-[10px] text-orange-400">Changed</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] text-green-400">Same</Badge>
                          )}
                        </div>
                        {diff.changed && field !== 'html_template' && (
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="bg-green-500/10 p-2 rounded">
                              <p className="text-green-400 mb-1">Version A:</p>
                              <p className="text-white truncate">{String(diff.version_a)}</p>
                            </div>
                            <div className="bg-blue-500/10 p-2 rounded">
                              <p className="text-blue-400 mb-1">Version B:</p>
                              <p className="text-white truncate">{String(diff.version_b)}</p>
                            </div>
                          </div>
                        )}
                        {diff.changed && field === 'html_template' && (
                          <div className="text-xs text-muted-foreground">
                            <p>A: {diff.version_a_length} chars</p>
                            <p>B: {diff.version_b_length} chars</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : selectedVersionPreview ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-white">
                      Preview - {selectedVersionPreview.is_current ? 'Current' : `v${selectedVersionPreview.version}`}
                    </h3>
                    {!selectedVersionPreview.is_current && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRevertToVersion(
                          selectedTemplate?.id,
                          versionHistory.history?.find(v => v.version === selectedVersionPreview.version)?.id,
                          selectedVersionPreview.version
                        )}
                        className="gap-2"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Revert to this version
                      </Button>
                    )}
                  </div>
                  <div className="bg-black/20 px-4 py-2 rounded border border-white/10">
                    <p className="text-xs text-muted-foreground">Subject:</p>
                    <p className="text-white">{selectedVersionPreview.subject}</p>
                  </div>
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <iframe
                      srcDoc={selectedVersionPreview.html}
                      className="w-full h-[400px] bg-white"
                      title="Version Preview"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <div className="text-center">
                    <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Select a version to preview</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
